import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { auth, signInWithGooglePopup } from "../services/firebase";
import {
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { db } from "../services/firebase";
import { doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { logFirebaseAccessError } from "../utils/firebasePermissionDebug";
import "./Auth.css";

const Auth = ({ onAuth }) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const inviteToken = queryParams.get("inviteToken") || "";
  const inviteRedirect = queryParams.get("redirect") || "";
  const requestedMode = queryParams.get("mode") || "";
  const requestedAccountType = queryParams.get("accountType") || "";
  const invitedEmail = queryParams.get("email") || "";
  const invitedCompanyId = queryParams.get("companyId") || "";
  const invitedCompanyName = queryParams.get("companyName") || "";
  const billingBypass = queryParams.get("billingBypass") || "";
  const isCompanyInviteSignup =
    queryParams.get("accountType") === "companyEmployee" &&
    billingBypass === "companySeat" &&
    Boolean(inviteToken);

  const [isSignUp, setIsSignUp] = useState(requestedMode === "signup");
  const [fullName, setFullName] = useState("");
  const [accountType, setAccountType] = useState(
    requestedAccountType === "company" ? "company" : "personal"
  );
  const [companyName, setCompanyName] = useState("");
  const [seatCount, setSeatCount] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stayLoggedIn, setStayLoggedIn] = useState(
    () => window.localStorage.getItem("manucalc_stay_logged_in") === "true"
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPersistence(
      auth,
      stayLoggedIn ? browserLocalPersistence : browserSessionPersistence
    ).catch((err) => {
      console.error("Error setting auth persistence:", err);
    });
  }, [stayLoggedIn]);

  const applyAuthPersistence = async () => {
    window.localStorage.setItem(
      "manucalc_stay_logged_in",
      stayLoggedIn ? "true" : "false"
    );
    await setPersistence(
      auth,
      stayLoggedIn ? browserLocalPersistence : browserSessionPersistence
    );
  };

  useEffect(() => {
    if (requestedMode === "signup") {
      setIsSignUp(true);
    }

    if (requestedAccountType === "company") {
      setAccountType("company");
    }
  }, [requestedAccountType, requestedMode]);

  useEffect(() => {
    if (!isCompanyInviteSignup) return;

    setIsSignUp(true);
    setAccountType("companyEmployee");
    setCompanyName(invitedCompanyName || "Company Workspace");

    if (invitedEmail) {
      setEmail(invitedEmail);
    }
  }, [isCompanyInviteSignup, invitedEmail, invitedCompanyName]);

  const getCompanySeatPricing = () => {
    const seats = Math.max(1, Number(seatCount) || 1);
    const extraSeats = Math.max(0, seats - 1);
    const extraSeatCost = extraSeats * 5;

    return {
      seats,
      extraSeats,
      extraSeatCost,
    };
  };

  const acceptCompanyInviteForUser = async (authUser) => {
    if (!isCompanyInviteSignup || !authUser?.uid || !inviteToken) return;

    const publicInviteRef = doc(db, "publicInvites", inviteToken);
    const publicInviteSnap = await getDoc(publicInviteRef);

    if (!publicInviteSnap.exists()) {
      throw new Error("Invite record was not found. Ask the company owner to send a new invite.");
    }

    const inviteData = publicInviteSnap.data() || {};
    const companyId = inviteData.companyId || invitedCompanyId;
    const linkedCompanyName = inviteData.companyName || invitedCompanyName || "Company Workspace";
    const role = inviteData.role || "viewer";
    const userEmail = String(authUser.email || "").toLowerCase();
    const inviteEmail = String(inviteData.email || invitedEmail || "").toLowerCase();

    if (inviteEmail && userEmail && inviteEmail !== userEmail) {
      throw new Error(`This invite was sent to ${inviteData.email}. Sign in with that email to accept it.`);
    }

    const memberData = {
      id: authUser.uid,
      uid: authUser.uid,
      username:
        authUser.displayName ||
        fullName ||
        String(authUser.email || "Team Member").split("@")[0],
      name:
        authUser.displayName ||
        fullName ||
        String(authUser.email || "Team Member").split("@")[0],
      email: authUser.email || inviteData.email || invitedEmail || "",
      role,
      status: "active",
      seatType: "Employee Seat",
      companyId,
      companyName: linkedCompanyName,
      companyOwnerUid: inviteData.companyOwnerUid || companyId,
      inviteToken,
      currentProject: "",
      currentTask: "",
      hoursWorked: 0,
      hourlyRate: 0,
      joinedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      await setDoc(
        doc(db, "companies", companyId, "members", authUser.uid),
        {
          ...memberData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      logFirebaseAccessError({
        error,
        path: `companies/${companyId}/members/${authUser.uid}`,
        operation: "create",
        user: authUser,
        role,
        companyId,
        context: "acceptCompanyInviteForUser member create",
      });
      throw error;
    }

    try {
      await setDoc(
        publicInviteRef,
        {
          status: "accepted",
          acceptedAt: serverTimestamp(),
          acceptedByUid: authUser.uid,
          acceptedByEmail: authUser.email || inviteData.email || invitedEmail || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      logFirebaseAccessError({
        error,
        path: `publicInvites/${inviteToken}`,
        operation: "update",
        user: authUser,
        role,
        companyId,
        context: "acceptCompanyInviteForUser public invite accept",
      });
      throw error;
    }

    await setDoc(
      doc(db, "companies", companyId, "invites", inviteToken),
      {
        ...inviteData,
        status: "accepted",
        acceptedAt: serverTimestamp(),
        acceptedByUid: authUser.uid,
        acceptedByEmail: authUser.email || inviteData.email || invitedEmail || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const createUserProfile = async (user, overrides = {}) => {
    const userDocRef = doc(db, "users", user.uid);
    const existingSnap = await getDoc(userDocRef);
    const existingData = existingSnap.exists() ? existingSnap.data() : null;

    const now = Date.now();

    const baseData = existingData?.trialStartDate
      ? {}
      : {
          trialStartDate: new Date(now).toISOString(),
          trialEndDate: new Date(now + 60 * 60 * 1000).toISOString(),
          subscriptionStatus: "trial",
        };

    const companySeatPricing = getCompanySeatPricing();
    const selectedAccountType = overrides.accountType || accountType;
    const isCompanyAccount = selectedAccountType === "company";
    const isCompanyEmployee = selectedAccountType === "companyEmployee";

    const displayName =
      user.displayName ||
      fullName ||
      String(user.email || "").split("@")[0];
    const companyId = isCompanyEmployee
      ? invitedCompanyId
      : isCompanyAccount
      ? user.uid
      : "";
    const cleanCompanyName = isCompanyEmployee
      ? invitedCompanyName
      : isCompanyAccount
      ? companyName.trim()
      : "";

    await setDoc(
      userDocRef,
      {
        email: user.email,
        username: displayName,
        displayName,
        accountType: isCompanyEmployee ? "company" : selectedAccountType,
        previousAccountType: isCompanyEmployee ? existingData?.accountType || "personal" : existingData?.previousAccountType || "",
        accountBillingType: isCompanyEmployee ? "companySeat" : isCompanyAccount ? "companyOwner" : "personal",
        billingExempt: isCompanyEmployee,
        subscriptionRequired: !isCompanyEmployee,
        role: isCompanyEmployee ? "viewer" : isCompanyAccount ? "owner" : "personal",
        companyId,
        companyName: cleanCompanyName,
        companyOwnerUid: isCompanyEmployee ? invitedCompanyId : isCompanyAccount ? user.uid : "",
        inviteToken: isCompanyEmployee ? inviteToken : "",
        seatCount: isCompanyAccount ? companySeatPricing.seats : 1,
        extraSeats: isCompanyAccount ? companySeatPricing.extraSeats : 0,
        extraSeatMonthlyCost: isCompanyAccount ? companySeatPricing.extraSeatCost : 0,
        adminDashboardEnabled: isCompanyAccount,
        createdAt: existingData?.createdAt || serverTimestamp(),
        ...baseData,
        ...overrides,
        ...(isCompanyEmployee
          ? {
              accountType: "company",
              accountBillingType: "companySeat",
              billingExempt: true,
              subscriptionRequired: false,
              subscriptionStatus: "active",
              trialStartDate: null,
              trialEndDate: null,
              role: "viewer",
              companyId: invitedCompanyId,
              companyName: invitedCompanyName,
              companyOwnerUid: invitedCompanyId,
              inviteToken,
              seatCount: 1,
              extraSeats: 0,
              extraSeatMonthlyCost: 0,
              adminDashboardEnabled: false,
            }
          : {}),
      },
      { merge: true }
    );

    if (isCompanyAccount) {
      const ownerMember = {
        id: user.uid,
        uid: user.uid,
        username: displayName,
        name: displayName,
        email: user.email || "",
        role: "owner",
        status: "active",
        seatType: "Owner Seat",
        companyId,
        companyName: cleanCompanyName,
        companyOwnerUid: user.uid,
        currentProject: `${cleanCompanyName || "Company"} Admin Setup`,
        currentTask: "Company workspace setup",
        hoursWorked: 0,
        hourlyRate: 0,
        joinedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(
        doc(db, "companies", companyId),
        {
          companyId,
          companyName: cleanCompanyName,
          ownerUid: user.uid,
          ownerEmail: user.email || "",
          seatCount: companySeatPricing.seats,
          extraSeats: companySeatPricing.extraSeats,
          extraSeatMonthlyCost: companySeatPricing.extraSeatCost,
          adminDashboardEnabled: true,
          subscriptionStatus: existingData?.subscriptionStatus || "trial",
          trialStartDate: existingData?.trialStartDate || baseData.trialStartDate,
          trialEndDate: existingData?.trialEndDate || baseData.trialEndDate,
          createdAt: existingData?.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "companies", companyId, "members", user.uid),
        {
          ...ownerMember,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await applyAuthPersistence();

      if (isSignUp) {
        if (accountType === "company" && !companyName.trim()) {
          setError("Please enter a company name.");
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        // ✅ Set Firebase Auth displayName
        if (fullName) {
          await updateProfile(userCredential.user, {
            displayName: fullName,
          });
        }

        await createUserProfile(userCredential.user, {
          accountType,
        });
        await acceptCompanyInviteForUser(userCredential.user);
        onAuth?.(userCredential.user);
        history.push(inviteRedirect || "/dashboard");
      } else {
        const result = await signInWithEmailAndPassword(auth, email, password);

        if (isCompanyInviteSignup) {
          await createUserProfile(result.user, {
            accountType: "companyEmployee",
          });
          await acceptCompanyInviteForUser(result.user);
        }

        onAuth?.(result.user);
        history.push(inviteRedirect || "/dashboard");
      }
    } catch (error) {
      console.error("Firebase auth error code:", error.code);
      console.error("Firebase auth error message:", error.message);

      if (isCompanyInviteSignup && error.code === "auth/email-already-in-use") {
        try {
          const existingUserResult = await signInWithEmailAndPassword(auth, email, password);

          await createUserProfile(existingUserResult.user, {
            accountType: "companyEmployee",
          });
          await acceptCompanyInviteForUser(existingUserResult.user);

          onAuth?.(existingUserResult.user);
          history.push(inviteRedirect || "/dashboard");
          return;
        } catch (signInError) {
          console.error("Existing invite account sign-in error code:", signInError.code);
          console.error("Existing invite account sign-in error message:", signInError.message);
          setIsSignUp(false);
          setError(
            "This email already has a ManuCalc account. Sign in with the existing password to join the company workspace."
          );
          return;
        }
      }

      setError(`${error.code}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);

    try {
      await applyAuthPersistence();

      const result = await signInWithGooglePopup();

      if (isSignUp || isCompanyInviteSignup) {
        if (accountType === "company" && !companyName.trim() && !isCompanyInviteSignup) {
          setError("Please enter a company name before continuing with Google.");
          setLoading(false);
          return;
        }

        await createUserProfile(result.user, {
          accountType: isCompanyInviteSignup ? "companyEmployee" : accountType,
        });
        await acceptCompanyInviteForUser(result.user);
      }

      onAuth?.(result.user);
      history.push(inviteRedirect || "/dashboard");
    } catch (error) {
      console.error("Google auth error code:", error.code);
      console.error("Google auth error message:", error.message);
      setError(`${error.code}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>{isCompanyInviteSignup ? "Create Employee Account" : isSignUp ? "Start Free Trial" : "Sign In"}</h1>
          <p>
            {isCompanyInviteSignup
              ? `Join ${invitedCompanyName || "your company"} on ManuCalc`
              : "Welcome to ManuCalc - Precision Machining Tools"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">

          {isSignUp && (
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                id="name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <label className="auth-remember-option">
            <input
              type="checkbox"
              checked={stayLoggedIn}
              onChange={(event) => setStayLoggedIn(event.target.checked)}
            />
            <span>Stay logged in on this device</span>
          </label>

          {isSignUp && (
            <>
              {!isCompanyInviteSignup && (
              <div className="form-group">
                <label>Account Type</label>
                <div className="auth-account-type-grid">
                  <button
                    type="button"
                    className={`auth-account-type-card ${accountType === "personal" ? "active" : ""}`}
                    onClick={() => setAccountType("personal")}
                  >
                    <strong>Personal</strong>
                    <span>One user seat for individual machinists, makers, and small-shop work.</span>
                  </button>

                  <button
                    type="button"
                    className={`auth-account-type-card ${accountType === "company" ? "active" : ""}`}
                    onClick={() => setAccountType("company")}
                  >
                    <strong>Company</strong>
                    <span>Owner/supervisor account with future Admin Dashboard access and employee seats.</span>
                  </button>
                </div>
              </div>

              )}

              {isCompanyInviteSignup && (
                <div className="auth-seat-summary">
                  <strong>Company employee seat</strong>
                  <span>
                    Your account will be attached to {invitedCompanyName || "this company"}. Billing is handled by the company owner.
                  </span>

                  <div
                    style={{
                      marginTop: "12px",
                      padding: "12px",
                      borderRadius: "12px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.04)",
                    }}
                  >
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      Connected Company
                    </strong>
                    <span>
                      {invitedCompanyName || "Company Workspace"}
                    </span>
                  </div>
                </div>
              )}

              {accountType === "company" && !isCompanyInviteSignup && (
                <>
                  <div className="form-group">
                    <label htmlFor="companyName">Company Name</label>
                    <input
                      id="companyName"
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required={accountType === "company"}
                      placeholder="Example: Summit Machine Works"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="seatCount">Total Seats</label>
                    <input
                      id="seatCount"
                      type="number"
                      min="1"
                      value={seatCount}
                      onChange={(e) => setSeatCount(Math.max(1, Number(e.target.value) || 1))}
                    />
                  </div>

                  <div className="auth-seat-summary">
                    <strong>{getCompanySeatPricing().seats} total seat(s)</strong>
                    <span>
                      Owner seat included. {getCompanySeatPricing().extraSeats} extra employee seat(s)
                      at $5/month each = ${getCompanySeatPricing().extraSeatCost}/month extra.
                    </span>
                  </div>
                </>
              )}

              <div className="auth-trial-note">
                {isCompanyInviteSignup
                  ? "No billing setup is required. This account uses a company employee seat."
                  : "Start a 1-hour free trial. No credit card required."}
              </div>
            </>
          )}

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading
              ? "Loading..."
              : isSignUp
              ? isCompanyInviteSignup
                ? "Continue to Company Workspace"
                : accountType === "company"
                ? "Start Company Trial"
                : "Start Free Trial"
              : "Sign In"}
          </button>
        </form>

        <div className="auth-divider">or</div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="auth-google"
          disabled={loading}
        >
          Continue with Google
        </button>

        <div className="auth-links">
          {isSignUp ? (
            <p>
              Already have an account?{" "}
              <button type="button" onClick={() => setIsSignUp(false)}>
                Sign in
              </button>
            </p>
          ) : (
            <p>
              Don&apos;t have an account?{" "}
              <button type="button" onClick={() => setIsSignUp(true)}>
                {isCompanyInviteSignup ? "Create employee account" : "Start free trial"}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
